import {
  createContext,
  FC,
  Fragment,
  useContext,
  useEffect,
  useState,
} from 'react'

import { useRouter } from 'next/router'

import find from 'lodash/find'

import useSocketIO from 'data/useSocketIO'
import useUserACL from 'data/useUserACL'

import ErrorAccessPermissionView from 'components/ErrorAccessPermissionView'
import VerifyingLoading from 'components/Loading/VerifyingLoading'
import PUBLIC_PAGES from 'utils/constants/page.constant'

export const PermissionContext = createContext({
  hasPageAccess: false,
  hasApproveAccess: false,
  hasReadAccess: false,
  hasWriteAccess: false,
  setPageKey: (_e: string) => {
    return
  },
  socketIO: null,
  hasFeatures: [],
  isAdminACL: false,
  hasOnlyWriteAccess: false,
})

export const usePermission = () => {
  return useContext(PermissionContext)
}

const PermissionContextProvider: FC = ({ children }) => {
  const router = useRouter()
  const [hasPageAccess, setPageAccess] = useState(false)
  const [hasApproveAccess, setApproveAccess] = useState(false)
  const [hasWriteAccess, setWriteAccess] = useState(false)
  const [hasReadAccess, setReadAccess] = useState(false)
  const [hasOnlyWriteAccess, setHasOnlyWriteAccess] = useState(false)
  const [isEverythingReady, setEverythingReady] = useState(false)
  const [hasFeatures, setHasFeatures] = useState([])
  const [isAdminACL, setIsAdminACL] = useState(false)

  const [pageKey, setPageKey] = useState('')
  const qUserACLs = useUserACL()
  const socketIO = useSocketIO({
    enabled:
      router.pathname ===
      '/in-season-management/sales-linkage-for-wholesale-sales/exhibition-order-monitoring',
  })

  const dataACLFeatureList = qUserACLs?.data
    ?.filter((item) => item.menu.includes(pageKey || router.route))
    .filter((feature) => feature.menu.includes('has-features'))

  useEffect(() => {
    if (qUserACLs.isFetched && router.isReady) {
      if (!qUserACLs.isSuccess) return setEverythingReady(true)
      const adminACL = find(qUserACLs.data, { menu: 'admin' })
      const route = pageKey || router.route
      const acl = find(qUserACLs.data, {
        menu: route,
      })

      if (acl) {
        setHasOnlyWriteAccess(acl.access_level === 'write')
      }

      if (acl) {
        setPageAccess(true)
        setReadAccess(acl.access_level === 'read')
        setWriteAccess(
          acl.access_level === 'write' || acl.access_level === 'approve',
        )

        setApproveAccess(
          acl.access_level === 'approve_only' || acl.access_level === 'approve',
        )

        if (dataACLFeatureList?.length > 0) {
          setHasFeatures(dataACLFeatureList)
        }
      }

      if (adminACL) {
        setPageAccess(true)
        setReadAccess(adminACL.access_level === 'read')
        setWriteAccess(
          adminACL.access_level === 'write' ||
            adminACL.access_level === 'approve',
        )

        setApproveAccess(
          adminACL.access_level === 'approve_only' ||
            adminACL.access_level === 'approve',
        )

        setIsAdminACL(true)
      }

      if (!acl && !adminACL) {
        setPageAccess(false)
        setReadAccess(false)
        setWriteAccess(false)
        setApproveAccess(false)
      }
      setEverythingReady(true)
    }
  }, [qUserACLs.isFetched, router.isReady, router.route, pageKey])

  if (!isEverythingReady && !PUBLIC_PAGES.includes(router.pathname)) {
    return <VerifyingLoading />
  }

  if (router.pathname === '/home') {
    return <Fragment>{children}</Fragment>
  }

  if (!hasPageAccess && !PUBLIC_PAGES.includes(router.pathname)) {
    return <ErrorAccessPermissionView />
  }

  return (
    <PermissionContext.Provider
      value={{
        hasApproveAccess,
        hasPageAccess,
        hasReadAccess,
        hasWriteAccess,
        setPageKey,
        socketIO: socketIO.data,
        hasFeatures,
        isAdminACL,
        hasOnlyWriteAccess,
      }}
    >
      {children}
    </PermissionContext.Provider>
  )
}

export default PermissionContextProvider
